import { DecimalPipe } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild, OnDestroy, NgZone, AfterViewInit } from '@angular/core';
import { RowNode, SelectionChangedEvent } from 'ag-grid-community';
import * as _ from 'underscore';
import { isNullOrUndefined, isNumber } from '../../models/utils';
import { ContextMenuOption } from '../../models/context-menu-option';
import { DeferredPromise } from '../../models/deferred-promise';
import { DrFilter } from '../../models/dr-filter';
import { DrRequestInfo } from '../../models/dr-request-info';
import { Endpoint, FilterAttribute, HelpIndex, MessageType, SearchType, ModalTargetType, SaveToRemoveFromOperationType, TagOperationType, UpdateStatusType } from '../../models/enums';
import { AlignDirection, CellActionType, GenericColDef, GridContext, GridRowSelectionType } from '../../models/grid-context';
import { ActionType, ObjectActionListType, Permissions } from '../../models/permission';
import { SearchResult } from '../../models/search-result';
import { OperationState, Task, TaskStatus, TaskType, WorkBasketContext } from '../../models/task';
import { ToggleItem } from '../../models/toggle-item';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { GenericGridComponent } from '../generic-grid/generic-grid.component';
import { AddToRemoveFromModalComponent, AddToRemoveFromModalData } from '../modals/add-to-remove-from-modal/add-to-remove-from-modal.component';
import { DeleteEntityModalComponent } from '../modals/delete-entity-modal/delete-entity-modal.component';
import { TagsModalComponent, TagsModalData } from '../modals/tags-modal/tags-modal.component';
import { WorkbasketTaskDetailsModalComponent } from '../modals/workbasket-task-details-modal/workbasket-task-details-modal.component';
import { TaskFilterComponent } from './task-filter/task-filter.component';
import { Subscription } from 'rxjs';
import { CreateManifestModalComponent } from '../modals/create-manifest/create-manifest.component';
import { NotificationModalComponent } from '../modals/notification-modal/notification-modal.component';
import { ReportType } from '../../models/report';
import { PagerComponent } from '../pager/pager.component';
import { DocumentFormat } from '../../models/dr-document';
import { DownloadAsPDFStampModalComponent } from '../modals/download-as-pdf-stamp-modal/download-as-pdf-stamp-modal.component';
import { DownloadService } from '../../services/download/download.service';

export enum MenuItem {
    ADDTAGS,
    REMOVETAGS,
    ADDTO,
    REMOVEFROM,
    FINDMORE,
    SEARCH,
    DOWNLOAD,
    NOTIFICATION,
    DETAILS,
    CANCEL,
    DELETE,
    CREATE_MANIFEST,
    DOWNLOAD_NATIVES,
    DOWNLOAD_AS_PDFS
}
export const searchesOnlyFilter = new DrFilter({ attribute: FilterAttribute.docBasedResultsOnly });
export const allUsersFilter = new DrFilter({ attribute: FilterAttribute.filteronowner });

@Component({
    standalone: false,
    selector: 'res-workbasket',
    templateUrl: './workbasket.component.html',
    styleUrls: ['./workbasket.component.scss']
})

export class WorkbasketComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() collapseCallback: () => void;
    @ViewChild(GenericGridComponent, { static: true }) grid: GenericGridComponent;
    @ViewChild(TaskFilterComponent, { static: true }) taskFilterControl: TaskFilterComponent;
    @ViewChild('downloadLink', { read: ElementRef }) downloadLink: ElementRef;

    automationId = 'workbasket';
    title = 'tasks';
    toggleFilters = [
        new ToggleItem({
            label: 'Tasks from all users',
            isChecked: false,
            data: allUsersFilter,
            tooltip: 'Display all users\' tasks',
            automationId: this.automationId + '-tasks-from-all-users'
        }),
        new ToggleItem({
            label: 'Searches only',
            isChecked: false,
            data: searchesOnlyFilter,
            tooltip: 'Display search results only',
            automationId: this.automationId + '-searches-only'
        })
    ];
    showHideBtnLabel = 'Show Filters';
    gridName = 'Tasks';
    pagerName = 'Work Basket';
    taskFilters = [];
    filtersOn: boolean;
    isExternalApp: boolean;
    currentPageIndex = 0;
    currentEmail: string;

    // Permissions
    hasAuditAccessPermissions: boolean;
    hasDocumentTagPermissions: boolean;
    hasProjectDataPermissions: boolean;
    hasDocumentFolderPermissions: boolean;
    hasDocumentCustodianPermissions: boolean;
    hasDocumentReportPermissions: boolean;
    hasWBAddEditPermissions: boolean;
    hasWBDeletePermissions: boolean;

    currentVisibleColumns = [];
    gridContext = new GridContext({ sortBy: 'dateStarted', sortAscending: false, sortEnabled: false });
    requestInfo: DrRequestInfo;
    columnDefs = new Array<GenericColDef>();
    defaultColumnDefs = new Array<GenericColDef>(
        {
            headerName: 'Status / Task',
            automationId: this.automationId,
            lockVisible: true,
            cellRenderer: 'gridWorkbasketActionsCellRenderer',
            field: 'description',
            minWidth: 100,
            cellRendererParams: {
                permissionControl: this.permissionControl.bind(this)
            },
        },
        {
            headerName: 'Results',
            automationId: this.automationId,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            cellStyle: { textAlign: 'right' },
            field: 'numberResults',
            width: 84,
            maxWidth: 90,
            minWidth: 60
        },
        {
            headerName: 'Started',
            automationId: this.automationId,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            field: 'dateStarted',
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            suppressSizeToFit: true,
            defaultSortBy: 'dateStarted',
            defaultSortAscending: false,
        },
        {
            headerName: 'Duration',
            automationId: this.automationId,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            field: 'duration',
            width: 75,
            hide: this.commonService.isExternalApp ? false : true
        },
        {
            headerName: '%',
            automationId: this.automationId,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            width: 55,
            minWidth: 55,
            maxWidth: 55,
            suppressSizeToFit: true,
            field: 'percentComplete',
        },
        {
            headerName: 'Finished',
            automationId: this.automationId,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            field: 'dateCompleted',
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            suppressSizeToFit: true,
            hide: this.commonService.isExternalApp ? false : true
        },
        {
            headerName: 'Owner',
            automationId: this.automationId,
            field: 'owner',
            width: 80,
            hide: this.commonService.isExternalApp ? false : true
        },
        {
            headerName: 'Task Code',
            automationId: this.automationId,
            field: 'task',
            hide: this.commonService.isExternalApp ? false : true
        }
    );

    autoUpdate = true;
    rowSelection = GridRowSelectionType.multiple;
    private selectedTasks: Task[] = [];
    private entityChangedEvent: Subscription;

    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Add Tags ...', iconClass: 'fa-tag', iconColor: 'color-grey-05',
            iconSecondClass: 'fa-plus', iconSecondColor: 'color-green-04',
            click: (item: Task) => this.contextMenuActions(MenuItem.ADDTAGS, item),
            visible: (item: Task) => {
                const visibleFlag = this.isSingleSelection() && item && item.isSearchType() && this.hasDocumentTagPermissions;
                if (item && item.viewType && item.viewType.includes('CORPUS')) {
                    return visibleFlag && this.hasProjectDataPermissions;
                } else {
                    return visibleFlag && !this.isTargetViewDiscardPile(item);
                }
            },
            enabled: (item: Task) => item.operationState !== OperationState.CANCELLED && item.operationState !== OperationState.WARNING &&
                item.operationState !== OperationState.FAILURE
        }),
        new ContextMenuOption({
            name: 'Remove Tags ...', iconClass: 'fa-tag', iconColor: 'color-grey-05',
            iconSecondClass: 'fa-minus', iconSecondColor: 'color-red-05',
            click: (item: Task) => this.contextMenuActions(MenuItem.REMOVETAGS, item),
            visible: (item: Task) => this.isSingleSelection() && item?.isSearchType() && this.hasDocumentTagPermissions && !item?.viewType?.includes('CORPUS') && !this.isTargetViewDiscardPile(item),
            enabled: (item: Task) => item.operationState !== OperationState.CANCELLED && item.operationState !== OperationState.WARNING &&
                item.operationState !== OperationState.FAILURE
        }),

        new ContextMenuOption({
            divider: true,
            visible: (item: Task) => this.isSingleSelection() && item && item.isSearchType() && !this.isTargetViewDiscardPile(item)
        }),

        new ContextMenuOption({
            name: 'Add to ...', iconClass: 'fa-folder', iconColor: 'color-tan-05',
            iconSecondClass: 'fa-plus', iconSecondColor: 'color-green-04',
            click: (item: Task) => this.contextMenuActions(MenuItem.ADDTO, item),
            visible: (item: Task) => {
                if (item && item.viewType && item.viewType.includes('CORPUS')) {
                    return this.isSingleSelection() && item && item.isSearchType()
                        && this.hasProjectDataPermissions && (this.hasDocumentFolderPermissions || this.hasDocumentCustodianPermissions);
                } else {
                    return this.isSingleSelection() && item && item.isSearchType() && (this.hasDocumentFolderPermissions || this.hasDocumentCustodianPermissions) && !this.isTargetViewDiscardPile(item);
                }
            },
            enabled: (item: Task) => item.operationState !== OperationState.CANCELLED && item.operationState !== OperationState.WARNING &&
                item.operationState !== OperationState.FAILURE
        }),
        new ContextMenuOption({
            name: 'Remove from ...', iconClass: 'fa-folder', iconColor: 'color-tan-05',
            iconSecondClass: 'fa-minus', iconSecondColor: 'color-red-05',
            click: (item: Task) => this.contextMenuActions(MenuItem.REMOVEFROM, item),
            visible: (item: Task) => this.isSingleSelection() && item?.isSearchType() && (this.hasDocumentFolderPermissions || this.hasDocumentCustodianPermissions) && !item?.viewType?.includes('CORPUS') && !this.isTargetViewDiscardPile(item),
            enabled: (item: Task) => item.operationState !== OperationState.CANCELLED && item.operationState !== OperationState.WARNING &&
                item.operationState !== OperationState.FAILURE
        }),

        new ContextMenuOption({
            divider: true,
            visible: (item: Task) => this.isSingleSelection() && item && item.isSearchType() && !this.isTargetViewDiscardPile(item)
        }),

        new ContextMenuOption({
            name: 'Find More Like These ...', iconClass: 'fa-search', iconColor: 'color-tan-07',
            click: (item: Task) => this.contextMenuActions(MenuItem.FINDMORE, item),
            visible: false,
            enabled: (item: Task) => item.operationState !== OperationState.CANCELLED && item.operationState !== OperationState.WARNING &&
                item.operationState !== OperationState.FAILURE
        }),
        new ContextMenuOption({
            name: 'Search In These Results ...', iconClass: 'fa-search-plus', iconColor: 'color-tan-07',
            click: (item: Task) => this.contextMenuActions(MenuItem.SEARCH, item),
            visible: false,
            enabled: (item: Task) => item.operationState !== OperationState.CANCELLED && item.operationState !== OperationState.WARNING &&
                item.operationState !== OperationState.FAILURE
        }),

        new ContextMenuOption({
            divider: true,
            visible: false
        }),

        new ContextMenuOption({
            name: 'Create Manifest ...', iconClass: 'fa-list-alt', iconColor: 'color-blue-01',
            enabled: (item) => (parseInt((item.numberResults + '').split(',').join('')) > 0), // Create manifest should be enabled in all tasks with number of results are greter than 0
            visible: (item) =>  // Create manifest should be visible in all tasks with number of results are greter than 0
            ((item.task === 'DOCUMENT_ADD_FROM_FILE_LIST') // and the task is a data set
                || (item.task === SearchType.METADATA_BULK) // or the task is a metadata bulk search
                || (item.task === SearchType.FREEFORM) // or the task is a Combined Search Result or Sample or Workflow Steps
                || (item.taskType === TaskType.SEARCH_BY_PROFILE) // or the task is 'Search By Profile'
                || (item.task === SearchType.DOCUMENT_FIND_DUPLICATES) // or Find Exact/Content Duplicates(view) Search Type
                || (item.task === SearchType.VECTOR_SET_FIND_DUPLICATES) // or Find Exact/Content duplicate groups
                || (item.taskType === TaskType.NEAR_DUPE) // Dupe Searches of a Document
                || (item.task === TaskType.COMPARE_EXPORT) // or an Export Comparison Report
                || (item.task === SearchType.COLLECTION_SIMILARITY) // or a more like these search
                || (item.task === 'DYNAMIC_FOLDER_RULES') // or Saved Searches
            ),
            tooltip: 'No results',
            tooltipAlwaysOn: false,
            click: (item: Task) => this.contextMenuActions(MenuItem.CREATE_MANIFEST, item)
        }),

        new ContextMenuOption({
            divider: true,
            visible: true
        }),

        new ContextMenuOption({
            name: 'Download All as PDFs', iconClass: 'fa-file-pdf-o', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-download', iconSecondColor: 'color-blue-01',
            visible: (item: Task) => this.isSingleSelection() && item && item.isSearchType(),
            enabled: (item: Task) => item.operationState !== OperationState.CANCELLED && item.operationState !== OperationState.WARNING &&
                item.operationState !== OperationState.FAILURE,
            click: (item: Task) => this.contextMenuActions(MenuItem.DOWNLOAD_AS_PDFS, item),
            permissions: [{ permissionType: ObjectActionListType.DOWNLOAD_PDF }]
        }),
        new ContextMenuOption({
            name: 'Download All Natives', iconClass: 'fa-file', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-download', iconSecondColor: 'color-blue-01',
            visible: (item: Task) => this.isSingleSelection() && item && item.isSearchType(),
            enabled: (item: Task) => item.operationState !== OperationState.CANCELLED && item.operationState !== OperationState.WARNING &&
                item.operationState !== OperationState.FAILURE,
            click: (item: Task) => this.contextMenuActions(MenuItem.DOWNLOAD_NATIVES, item),
            permissions: [{ permissionType: ObjectActionListType.DOWNLOAD_NATIVES }]
        }),
        new ContextMenuOption({ divider: true, visible: (item: Task) => this.isSingleSelection() && item && item.isSearchType() }),

        new ContextMenuOption({
            name: 'Download', iconClass: 'fa-download', iconColor: 'color-blue-01',
            click: (item: Task) => this.contextMenuActions(MenuItem.DOWNLOAD, item),
            visible: (item: Task) => this.isSingleSelection() && item && item.isDownloadType() && this.hasDocumentReportPermissions,
            enabled: (item: Task) => (item.taskStatus !== TaskStatus.FAILURE || (item.taskStatus === TaskStatus.FAILURE && item.warnings)) &&
                item.operationState !== OperationState.FAILURE && item.operationState !== OperationState.CANCELLED
        }),

        new ContextMenuOption({
            divider: true,
            visible: (item: Task) => this.isSingleSelection() && item && item.isDownloadType()
        }),

        new ContextMenuOption({
            name: 'Task Notification ...', iconClass: 'fa-bell-o', iconColor: 'color-grey-10',
            click: (item: Task) => this.contextMenuActions(MenuItem.NOTIFICATION, item),
            visible: (item: Task) => this.isSingleSelection() && this.hasWBAddEditPermissions,
            enabled: (item: Task) => (item.operationState === OperationState.RUNNING || item.taskStatus === TaskStatus.IN_PROGRESS || item.taskStatus === TaskStatus.PAUSED ||
                item.operationState === OperationState.BLOCKED || item.operationState === OperationState.QUEUED || item.taskStatus === TaskStatus.PAUSED_ON_FAILURE)
        }),

        new ContextMenuOption({
            name: 'Task Details ...', iconClass: 'fa-info', iconColor: 'color-grey-10',
            click: (item: Task) => this.contextMenuActions(MenuItem.DETAILS, item),
            visible: (item: Task) => this.isSingleSelection()
        }),

        new ContextMenuOption({
            divider: true,
            visible: (item: Task) => this.isSingleSelection()
        }),

        new ContextMenuOption({
            name: 'Cancel Task', iconClass: 'fa-close', iconColor: 'color-grey-10',
            click: (item: Task) => this.contextMenuActions(MenuItem.CANCEL, item),
            visible: (item: Task) => this.isSingleSelection() && this.hasWBAddEditPermissions,
            enabled: (item: Task) => item.operationState === OperationState.QUEUED ||
                item.operationState === OperationState.BLOCKED ||
                item.operationState === OperationState.RUNNING ||
                item.taskStatus === TaskStatus.IN_PROGRESS ||
                item.taskStatus === TaskStatus.PAUSED ||
                item.taskStatus === TaskStatus.PAUSED_ON_FAILURE
        }),
        new ContextMenuOption({
            name: 'Delete Task(s)', iconClass: 'fa-trash', iconColor: 'color-red-05',
            click: (item: Task) => this.contextMenuActions(MenuItem.DELETE, item),
            enabled: (item: Task) => this.allDeleteable(item),
            visible: (item: Task) => this.hasWBDeletePermissions
        })
    ];

    updateStatusType = UpdateStatusType.TASK;

    permissionControl(action: string): boolean {
        if (action === 'Pause' || action === 'Resume' || action === 'Cancel') {
            return this.hasWBAddEditPermissions;
        } else if (action === 'Delete') {
            return this.hasWBDeletePermissions;
        }
    }

    cellClickedCallback = (rowNode: RowNode, action: CellActionType): void => {
        if (rowNode && rowNode.data instanceof Task && action) {
            if (action === CellActionType.workbasketIcon) {
                this.iconClick(rowNode.data);
            } else if (action === CellActionType.workbasketPause) {
                this.pause(rowNode.data);
            } else if (action === CellActionType.workbasketResume) {
                this.resume(rowNode.data);
            } else if (action === CellActionType.workbasketCancel) {
                this.cancel(rowNode.data);
            } else if (action === CellActionType.workbasketDelete) {
                this.delete(rowNode.data);
            }
        }
    }

    cellDoubleClickedCallback = (rowNode: RowNode): void => {
        if (rowNode && rowNode.data) {
            const task = rowNode.data as Task;
            const exportComparisonSearchView = _.find(task.attributes, (attribute) => attribute.name === 'exportComparisonSearchView');
            switch (task.taskType) {
                case TaskType.SEARCH:
                case TaskType.SEARCH_BY_DOCUMENT:
                case TaskType.SEARCH_BY_PROFILE:
                case TaskType.SEARCH_FOR_DUPLICATE_DOCUMENTS:
                case TaskType.NEAR_DUPE:
                    this.preNavigate(task);
                    break;
                case TaskType.COMPARE_EXPORT:
                    if (exportComparisonSearchView) {
                        this.preNavigate(new Task({ handle: exportComparisonSearchView.value }), task);
                    }
                    break;
                case TaskType.OTHER:
                    if (['SEARCH_FROM_BULK_SEARCH', 'BULK_SEARCH', 'METADATA_BULK_SEARCH', 'COLLECTION_SIMILARITY', 'VECTOR_SET_FIND_DUPLICATES', 'DOCUMENT_FIND_DUPLICATES'].includes(task.task)) {
                        this.preNavigate(task);
                        break;
                    }
            }
        }
    }

    ngOnInit(): void {
        if (this.commonService.currentProject) {
            this.requestInfo = new DrRequestInfo({
                endpoint: Endpoint.projectManager_listTasks,
                data: {
                    projectHandle: this.commonService.currentProject.handle,
                    selectedAttributes: ['includesavedsearches'],
                    filters: []
                },
                responseName: 'tasks'
            });
        }
        // "Tasks from all users" - Audit Access permission
        this.hasAuditAccessPermissions = Permissions.checkUserPermission(ObjectActionListType.AUDIT_LOG);

        if (!this.hasAuditAccessPermissions) {
            this.toggleFilters.splice(0, 1);
        }

        // Workbasket Context Menu permissions
        this.hasDocumentTagPermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.DOCUMENT_TAGS,
            permissionActions: [ActionType.CREATE]
        }]);

        this.hasProjectDataPermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.ECA_CASE_DATA,
            permissionActions: [ActionType.CREATE]
        }]);

        this.hasDocumentFolderPermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.DOCUMENT_FOLDER,
            permissionActions: [ActionType.CREATE]
        }]);

        this.hasDocumentCustodianPermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.DOCUMENT_CUSTODIAN,
            permissionActions: [ActionType.CREATE]
        }]);

        this.hasDocumentReportPermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.DOCUMENT_REPORTS,
            permissionActions: [ActionType.VIEW]
        }]);

        this.hasWBAddEditPermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.WORK_BASKET,
            permissionActions: [ActionType.CREATE]
        }]);

        this.hasWBDeletePermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.WORK_BASKET,
            permissionActions: [ActionType.DELETE]
        }]);
        this.isExternalApp = this.commonService.isExternalApp && !isNullOrUndefined(this.commonService.workBasketContext);

        if (this.isExternalApp) {
            this.columnDefs = this.defaultColumnDefs;
            this.showHideBtnLabel = this.commonService.workBasketContext.showFilters ? 'Show Filters' : 'Hide Filters';

            if (this.hasAuditAccessPermissions) {
                this.toggleFilters[0].isChecked = this.commonService.workBasketContext.allUsersChecked;
                this.toggleFilters[1].isChecked = this.commonService.workBasketContext.searchesOnlyChecked;
            } else {
                this.toggleFilters[0].isChecked = this.commonService.workBasketContext.searchesOnlyChecked;
            }

            let filterList = this.commonService.workBasketContext.taskFilters;
            filterList = filterList.filter(f => f.attribute !== FilterAttribute.filteronowner && f.attribute !== FilterAttribute.docBasedResultsOnly);
            if (filterList && filterList.length > 0) {
                this.taskFilterControl.filterObjectToRestore = this.commonService.workBasketContext.filterObject;
                this.taskFilterControl.selectedFiltersToRestore = filterList;
                this.filtersOn = this.commonService.workBasketContext.formSubmitted;
            }
            this.requestInfo.data = this.commonService.workBasketContext.requestInfoData;
            this.taskFilters = this.commonService.currentWorkbasketFilters;
        } else {
            if (this.commonService.workBasketInfoToRestore) {
                if (this.commonService.workBasketInfoToRestore.taskFilters) {
                    this.restoreTaskFiltersFromObject(this.commonService.workBasketInfoToRestore.taskFilters);
                }
                if (this.commonService.workBasketInfoToRestore.visibleColumns) {
                    this.restoreVisibleColumns(this.commonService.workBasketInfoToRestore.visibleColumns);
                } else {
                    this.columnDefs = this.defaultColumnDefs;
                }
            } else {
                this.columnDefs = this.defaultColumnDefs;
            }
        }
    }

    ngAfterViewInit(): void {
        if (this.isExternalApp && this.taskFilterControl) {
            this.taskFilterControl.isOwnerVisible = this.toggleFilters[0].label === 'Tasks from all users' && this.toggleFilters[0].isChecked;
            this.taskFilterControl.isFormSubmitted = this.commonService.workBasketContext.formSubmitted;
            this.taskFilters = this.commonService.workBasketContext.taskFilters;
        }
    }

    private restoreTaskFiltersFromObject(filterList: any): void {
        if (filterList && filterList.length > 0) {
            filterList.forEach(filterElement => {
                if (filterElement === allUsersFilter || filterElement === searchesOnlyFilter) {
                    const idx = filterElement === allUsersFilter ? 0 : 1;
                    this.toggleFilters[idx].isChecked = true;
                    if (this.taskFilterControl && this.toggleFilters[0].label === 'Tasks from all users' && this.toggleFilters[0].isChecked) {
                        // Owner should be visible only when all users tasks is checked
                        this.taskFilterControl.isOwnerVisible = true;
                    }
                }
            });
            filterList = filterList.filter(f => f.attribute !== FilterAttribute.filteronowner && f.attribute !== FilterAttribute.docBasedResultsOnly);
            if (filterList && filterList.length > 0) {
                this.filtersOn = true;
                this.taskFilterControl.selectedFiltersToRestore = filterList;
                this.taskFilterControl.isFormSubmitted = true;
            }
            this.taskFilterControl.filterObjectToRestore = this.commonService.workBasketInfoToRestore.filterObjectToRestore;
            this.onApplyFilters(filterList);
        }
    }

    private restoreVisibleColumns(visibleColumns: any): void {
        if (visibleColumns && visibleColumns.length) {
            const colDefsToShow = new Array<GenericColDef>();
            visibleColumns.forEach(fieldName => {
                const columnToShow = this.defaultColumnDefs.find(c => c.field === fieldName);
                if (columnToShow) {
                    columnToShow.hide = false;
                    colDefsToShow.push(columnToShow);
                }
            });
            this.columnDefs.push(...colDefsToShow);
        }
    }

    onColumnVisibilityChanged(): void {
        if (this.grid?.gridApi?.getColumns()?.length > 0) {
            const currentColumns = this.grid.gridApi.getColumns().filter(c => c.isVisible() && !isNullOrUndefined(c.getColDef().field));
            if (currentColumns && currentColumns.length > 0) {
                this.currentVisibleColumns = currentColumns.map(c => c.getColDef().field);
            }
        }
    }

    private preNavigate(task: Task, child?: any): void {
        this.restService.post(Endpoint.taskManager_getTasks, {
            taskHandles: [task.handle],
            includeDrDebug: true,
            includeResourceStatistics: true
        }).then(response => {
            if (response.tasks && response.tasks.length > 0) {
                const parent = new SearchResult(Object.assign({ handle: task.handle, documentCount: response.tasks[0].numberResults, entityType: UpdateStatusType.RESULT_VIEW, viewType: task.viewType },
                    response.tasks[0]));
                const bundle = child ? [parent, child] : parent;
                if (this.commonService.isExternalApp) {
                    window.opener.navigateTo(bundle);
                } else {
                    this.commonService.sendMessage(MessageType.navigationRequest, bundle);
                }
            }
        });
    }

    onSelectionChanged = (event?: SelectionChangedEvent): void => {
        if (event && event.api) {
            this.selectedTasks = event.api.getSelectedRows();
        }
    }

    responseArrayCallback = (value: any): Promise<Task> => {
        const request = new DeferredPromise<Task>();
        if (!isNullOrUndefined(value.description)) {
            value.description = value.description.replace('Case Data', 'Project Data').replace('Updating dataset', 'Reindexing Data Set').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/'/g, '&apos;');
        }
        if (parseInt(value.numberResults, 10) > 0) {
            value.numberResults = isNumber(value.numberResults) ? new DecimalPipe('en-us').transform(value.numberResults, '1.0-0') : value.numberResults;
        } else {
            value.numberResults = '0';
        }

        let diffStr = '';
        let diffSeconds = value.secondsElapsed;
        const days = Math.floor(diffSeconds / 86400);
        diffSeconds = diffSeconds % 86400;
        if (days > 0) {
            diffStr = days + (diffSeconds > 0 ? 'd ' : 'd');
        }

        const hours = Math.floor(diffSeconds / 3600);
        diffSeconds = diffSeconds % 3600;
        if (days > 0 || hours > 0) {
            if (days > 0 && hours < 10) {
                diffStr += '0';
            }
            diffStr += hours + ':';
        }

        const minutes = Math.floor(diffSeconds / 60);
        diffSeconds = diffSeconds % 60;
        if (days > 0 || hours > 0 || minutes > 0) {
            if ((days > 0 || hours > 0) && minutes < 10) {
                diffStr += '0';
            }
            diffStr += minutes;
        }

        diffStr += ((diffSeconds < 10) ? ':0' : ':') + diffSeconds;
        value.duration = diffStr;

        value.dateCompleted = this.commonService.insertYesterday(value.dateCompleted);
        value.dateStarted = this.commonService.insertYesterday(value.dateStarted);

        request.resolve(new Task(value));
        return request.promise;
    }

    constructor(private commonService: CommonService, private restService: RestService, private ngZone: NgZone, private downloadService: DownloadService) {
        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            if (data === UpdateStatusType.TASK && this.updateStatusType !== null) {
                this.grid.loadData(this.requestInfo?.data?.startIndex ? this.requestInfo.data.startIndex : 0, PagerComponent.itemsOnPage, true);
            } else if (data && data.updateStatusType === UpdateStatusType.TASK && this.updateStatusType !== null) {
                this.grid.loadData(0, 0, true, data.response);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.commonService.documentViewerOpenContext) {
            this.commonService.workBasketInfoToRestore = {
                taskFilters: this.taskFilters && this.taskFilters.length > 0 ? this.taskFilters : null,
                visibleColumns: this.currentVisibleColumns.length > 0 ? this.currentVisibleColumns : null,
                filterObjectToRestore: this.taskFilterControl.filterObject
            };
        }
        this.entityChangedEvent.unsubscribe();
        this.commonService.currentWorkbasketFilters = null;
    }

    showHideFilters(): void {
        if (this.showFilters()) {
            this.showHideBtnLabel = this.showHideBtnLabel.replace('Show', 'Hide');
        } else {
            this.showHideBtnLabel = this.showHideBtnLabel.replace('Hide', 'Show');
        }
    }

    showFilters(): boolean {
        return this.showHideBtnLabel.startsWith('Show');
    }

    isAlreadySelected(task: Task): boolean {
        return _.some(this.selectedTasks, (item) => item.handle === task.handle);
    }

    isSingleSelection(): boolean {
        return this.selectedTasks.length <= 1;
    }

    isTargetViewDiscardPile(task:Task): boolean {
      return _.find(task.attributes, attr => attr.name === 'targetViewName' && attr.value?.includes('Discard Pile'));
    }

    allDeleteable(selectedTask: Task): boolean {
        if (this.isAlreadySelected(selectedTask)) {
            let visible = true;
            this.selectedTasks.forEach((task: Task) => {
                visible = visible && task && task instanceof Task && task.isDeleteable();
            });
            return visible;
        } else {
            return selectedTask && selectedTask instanceof Task && selectedTask.isDeleteable();
        }
    }

    contextMenuActions(actionType: MenuItem, rowData: Task): void {
        switch (actionType) {
            case MenuItem.ADDTAGS:
                this.commonService.openModal(TagsModalComponent, {
                    modalData: <TagsModalData>{
                        targets: [rowData],
                        targetType: ModalTargetType.view,
                        operationType: TagOperationType.tag,
                        updateStatusType: UpdateStatusType.TASK,
                        name: rowData.description,
                        handle: this.commonService.projectDataHandle
                    }
                });
                break;
            case MenuItem.REMOVETAGS:
                this.commonService.openModal(TagsModalComponent, {
                    modalData: <TagsModalData>{
                        targets: [rowData],
                        targetType: ModalTargetType.view,
                        operationType: TagOperationType.untag,
                        updateStatusType: UpdateStatusType.TASK,
                        name: rowData.description,
                        handle: this.commonService.projectDataHandle
                    }
                });
                break;
            case MenuItem.ADDTO: this.commonService.openModal(AddToRemoveFromModalComponent, {
                modalData: <AddToRemoveFromModalData>{
                    targets: [rowData],
                    operationType: SaveToRemoveFromOperationType.add,
                    targetType: ModalTargetType.view,
                    name: rowData.description
                }, class: 'horizontal-px-700'
            });
                break;
            case MenuItem.REMOVEFROM: this.commonService.openModal(AddToRemoveFromModalComponent, {
                modalData: <AddToRemoveFromModalData>{
                    targets: [rowData],
                    operationType: SaveToRemoveFromOperationType.remove,
                    targetType: ModalTargetType.view,
                    name: rowData.description
                }, class: 'horizontal-px-700'
            });
                break;
            case MenuItem.FINDMORE: // ECASL-27012
            case MenuItem.SEARCH:
                console.error('Menu item not implemented yet');
                break;
            case MenuItem.CREATE_MANIFEST:
                this.commonService.openModal(CreateManifestModalComponent, {
                    modalData: {
                        entity: rowData,
                    }, class: 'vertical-percent-100 horizontal-percent-100',
                    backdrop: false, keyboard: false
                });
                break;
            case MenuItem.DOWNLOAD:
                this.download(rowData);
                break;
            case MenuItem.NOTIFICATION:
                if (this.commonService.currentUser.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.type === 'System User') {
                    this.restService.post(Endpoint.userManager_getOrgUserbyName, {
                        handle: this.commonService.currentUser.userName,
                        orgName: 'super_system_customer'
                    }).then(response => {
                        if (response) {
                            this.currentEmail = response.user.email;
                            this.commonService.openModal(NotificationModalComponent, {
                                modalData: { item: [rowData], modalType: 'job-notification', isFromWorkbasket: true, currentEmail: this.currentEmail }
                            }).then(data => {
                                const emails = data?.modalData?.emails.slice(0, -1).filter(email => email.trim() !== '');
                                this.commonService.openInformationToastNotification(`Task notification will be Emailed to: ${this.displayEmails(emails)}.`);
                            });
                        }
                    });
                } else {
                    this.commonService.openModal(NotificationModalComponent, {
                        modalData: { item: [rowData], modalType: 'job-notification', isFromWorkbasket: true, }
                    }).then(data => {
                        const emails = data?.modalData?.emails.slice(0, -1).filter(email => email.trim() !== '');
                        this.commonService.openInformationToastNotification(`Task notification will be Emailed to: ${this.displayEmails(emails)}.`);
                    });
                }
                break;
            case MenuItem.DETAILS:
                this.restService.post(Endpoint.taskManager_getTasks, {
                    taskHandles: [rowData.handle],
                    includeDrDebug: true,
                    includeResourceStatistics: true
                }).then(response => {
                    const viewType = _.find(response.tasks[0].attributes, attr => attr.name === 'viewtype');
                    this.commonService.openModal(WorkbasketTaskDetailsModalComponent, {
                        modalData: {
                            taskDetails: response.tasks[0].currentStatus,
                            viewType: viewType ? viewType.value : null
                        },
                        class: 'vertical-px-500 horizontal-px-1000'
                    });
                });
                break;
            case MenuItem.CANCEL:
                this.cancel(rowData);
                break;
            case MenuItem.DELETE:
                this.delete(this.selectedTasks.length > 0 ? this.selectedTasks : rowData);
                break;
            case MenuItem.DOWNLOAD_NATIVES: {
                this.downloadDocumentsFromView(rowData.viewHandle ? rowData.viewHandle : rowData.handle);
                break;
            }
            case MenuItem.DOWNLOAD_AS_PDFS: {
                const viewHandle = rowData.viewHandle ? rowData.viewHandle : rowData.handle;
                if (viewHandle) {
                    this.commonService.openModal(DownloadAsPDFStampModalComponent, { class: 'horizontal-px-400' }).then(response => {
                        if (response?.modalResult && response.modalData) {
                            this.downloadDocumentsFromView(viewHandle, true, response.modalData.isStampEnabled);
                        }
                    });
                }
                break;
            }
        }
    }

    downloadDocumentsFromView(viewHandle: string, isPdfMode = false, isStampEnabled = false) {
        if (viewHandle) {
            const docName1 = isPdfMode ? 'documents' : 'natives';
            const docName2 = isPdfMode ? 'PDFs' : 'natives';
            this.commonService.openConfirmationWarningModal(`You are attempting to download all ${docName1} in this list ${isPdfMode ? 'as PDFs' : ''}. Depending on the size of the documents, this could take considerable time and/or render the browser unresponsive. Consider creating a new export stream to produce the ${docName2} directly to an export location instead.`,
                'Warning', 'Continue', 'Cancel', null, 'custom-size-message').then(result => {
                    if (result.modalResult) {
                        this.restService.post(Endpoint.documentManager_retrieveDocuments, {
                            viewHandle: viewHandle,
                            documentInfos: null,
                            format: isPdfMode ? DocumentFormat.PDF : DocumentFormat.NATIVE,
                            applyStamp: isStampEnabled
                        });
                        this.commonService.openInformationToastNotification(`The ${isPdfMode ? 'PDFs' : 'documents'} are being prepared for downloading. Once finished, the archive (.zip) can be downloaded from the Work Basket.`);
                    }
                });
        }
    }

    download(task: Task): void {
        this.downloadService.download(task, this.downloadLink);
    }

    pause(task: Task): void {
        this.restService.post(Endpoint.taskManager_pauseTask, { taskHandle: task.handle }).then(response => {
            this.commonService.sendMessage(MessageType.entityChanged, { updateStatusType: UpdateStatusType.TASK });
            if (task.task === 'VALIDATE_BULK_SEARCH' && task.taskStatus === 'IN_PROGRESS') {
                this.commonService.sendMessage(MessageType.cancelBulkSearch);
            }
        }, error => {
            console.error('Unable to pause task' + error);
        });
    }

    resume(task: Task): void {
        this.restService.post(Endpoint.taskManager_resumeTask, { taskHandle: task.handle }).then(response => {
            this.commonService.sendMessage(MessageType.entityChanged, { updateStatusType: UpdateStatusType.TASK });
        }, error => {
            console.error('Unable to resume task');
        });
    }

    cancel(task: Task): void {
        this.commonService.openConfirmationModal('Cancel "' + (task as Task).description.replace(/&lt;/g, '<').replace(/&gt;/g, '>') + '"?', 'Cancel Task', 'Cancel Task', `Don't Cancel Task`).then(result => {
            if (result.modalResult) {
                this.restService.post(Endpoint.taskManager_cancelTask, { taskHandle: task.handle }).then(response => {
                    this.commonService.sendMessage(MessageType.entityChanged, { updateStatusType: UpdateStatusType.TASK });

                    if (task.task === 'VALIDATE_BULK_SEARCH' && task.taskStatus === 'IN_PROGRESS') {
                        this.commonService.sendMessage(MessageType.cancelBulkSearch);
                    }
                }, error => {
                    console.error('Unable to cancel task' + error);
                });
            }
        });
    }

    delete(task: Task | Task[]): void {
        this.commonService.openModal(DeleteEntityModalComponent, { modalData: task });
    }

    iconClick(task: Task): void {
        if (task && (task.taskStatus === TaskStatus.FAILURE || task.taskStatus === TaskStatus.PAUSED_ON_FAILURE)) {
            this.restService.post(Endpoint.taskManager_getTasks, {
                taskHandles: [task.handle],
                includeDrDebug: true,
                includeResourceStatistics: true
            }).then(response => {
                const error = response && response.tasks && response.tasks[0] && response.tasks[0].extendedStatus ?
                    response.tasks[0].extendedStatus : 'Error information is unavailable.';
                this.commonService.openErrorModal(error, 'Task Failed', false);
            });
        } else if (task && task.isDownloadType()) {
            this.download(task);
        } else if (task && task.taskStatus === TaskStatus.SUCCESS && task.operationState === OperationState.WARNING) {
            this.commonService.openConfirmationInfoModal('Task completed with Warnings/Errors. Download the Warnings Report?',
                'Download Report', 'Download Report (.csv)', 'Cancel').then(result => {
                    if (result.modalResult) {
                        this.download(task);
                    }
                });
        }
    }

    notify(): void {
        this.commonService.sendMessage(MessageType.entityChanged, { updateStatusType: UpdateStatusType.TASK });
    }

    spawnWindow(): void {
        this.taskFilterControl.setSelectedFilterList();
        const workBasketContext = new WorkBasketContext({
            taskFilters: this.taskFilterControl.selectedFilters,
            requestInfoData: this.requestInfo.data,
            showFilters: this.showFilters(),
            formSubmitted: this.taskFilterControl.isFormSubmitted,
            filterObject: this.taskFilterControl.filterObject
        })
        if (this.hasAuditAccessPermissions) {
            workBasketContext.allUsersChecked = this.toggleFilters[0].isChecked;
            workBasketContext.searchesOnlyChecked = this.toggleFilters[1].isChecked;
        } else {
            workBasketContext.searchesOnlyChecked = this.toggleFilters[0].isChecked;
        }
        this.commonService.openExternalWorkbasket(workBasketContext);
        if (this.collapseCallback) {
            this.collapseCallback();
        }
    }

    openHelp = (): void => this.commonService.openHelp(HelpIndex.myedisc_work_basket_new_dlg);

    autoUpdateChanged(): void {
        if (this.autoUpdate) {
            this.updateStatusType = UpdateStatusType.TASK;
        } else {
            this.updateStatusType = null;
        }
    }

    isAllUsersFilterChecked(): boolean {
        return this.toggleFilters[0].isChecked;
    }

    // toggle filters were changed, reload grid data with updated filters
    onFilterToggleChanged(changedToggle: ToggleItem): void {
        if (changedToggle) {
            const filterToggle = this.toggleFilters.find(t => {
                return changedToggle.label === t.label;
            });
            if (filterToggle) {
                const index = this.taskFilters.findIndex(tf => {
                    return tf.attribute === filterToggle.data.attribute;
                });
                const allFilterTogglesChecked = this.toggleFilters.every(t => t.isChecked);
                if (changedToggle.isChecked) {
                    if (index === -1) {
                        this.taskFilters.push(filterToggle.data);
                    }
                    this.currentPageIndex = allFilterTogglesChecked ? 0 : this.requestInfo.data.startIndex;
                    this.requestInfo.data.startIndex = 0;
                } else {
                    this.taskFilters.splice(index, 1);
                    if (this.taskFilters.length === 0) {
                        this.requestInfo.data.startIndex = this.currentPageIndex;
                    } else if (!allFilterTogglesChecked) {
                        this.requestInfo.data.startIndex = 0;
                    }
                }
                this.requestInfo.data.filters = this.taskFilters;
                this.commonService.currentWorkbasketFilters = this.taskFilters;
                this.grid.loadData();
                if (this.taskFilterControl && filterToggle.label === 'Tasks from all users' && filterToggle === this.toggleFilters[0]) {
                    // Owner should be visible only when all users tasks is checked
                    this.taskFilterControl.isOwnerVisible = filterToggle.isChecked;
                }
            }
        }
    }

    onApplyFilters(filterList: any): void {
        this.taskFilters = [];
        if (filterList && filterList.length > 0) {
            this.taskFilters = filterList;
            this.filtersOn = true;
            this.currentPageIndex = this.requestInfo.data.startIndex;
        } else {
            this.filtersOn = false;
        }

        this.toggleFilters.forEach(toggleFilter => {
            if (toggleFilter.isChecked) {
                this.taskFilters.push(toggleFilter.data);
            }
        });

        if (this.taskFilters.length > 0) {
            this.requestInfo.data.startIndex = 0;
        } else {
            this.requestInfo.data.startIndex = this.currentPageIndex;
        }
        this.requestInfo.data.filters = this.taskFilters;
        this.commonService.currentWorkbasketFilters = this.taskFilters;

        this.grid.loadData();
    }

    private displayEmails(emails: string[]): string {
        return emails.length > 3 ? `${emails.slice(0, 3).join('; ')} and others` : emails.join('; ');
    }
}
